import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './CompanySearch.module.scss';
import { searchCompany } from '@src/api/Referencial';
import Input from '../Input';
import ChrevronRight from '@src/assets/ChevronRight.png';
import Loader from '../Loader';

interface Instrument {
    name: string;
    isin: string;
    ticker: string;
}

type CompanySearchProps = {
    setNotFound: React.Dispatch<React.SetStateAction<boolean>>;
    setCompany: React.Dispatch<React.SetStateAction<any>>;
    selectedCompanyName: string;
}

const CompanySearch: React.FC<CompanySearchProps> = ({ setNotFound, setCompany, selectedCompanyName }) => {
    const [query, setQuery] = useState(selectedCompanyName || '');
    const [debouncedQuery, setDebouncedQuery] = useState<string>('');
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { data: suggestions, refetch, isLoading } = useQuery<{instruments: Instrument[]}>({
        queryKey: [`searchCompany`, debouncedQuery],
        queryFn: () => searchCompany({ searchValue: debouncedQuery }),
        enabled: false
    });

    useEffect(() => {
        if (debouncedQuery && debouncedQuery.trim() !== '' && !selected) {
            refetch();
            setIsSuggestionsOpen(true);
        } else {
            setIsSuggestionsOpen(false);
        }
    }, [debouncedQuery, refetch, selected]);

    const handleSelectCompany = (instrument: Instrument): void => {
        setCompany({
            name: instrument.name, 
            isin: instrument.isin
        });
        setQuery(instrument.name);
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(true);
    }

    const handleClear = (): void => {
        setCompany({
            name: '',
            isin: ''
        });
        setQuery('');
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(false);
        setNotFound(false);
    }

    const handleNotFound = (): void => {
        setNotFound(true);
        setIsSuggestionsOpen(false);
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setQuery(e.target.value);
        setSelected(false);
    };

    const renderNotFoundButton = (): JSX.Element => (
        <li>
            <button className={styles.notFoundButton} onClick={handleNotFound}>
                <p className={styles.notFoundText}>La société n'est pas ici</p>
                <img src={ChrevronRight} alt="ChrevronRight" width={7} height={12} />
            </button>
        </li>
    );

    const renderSuggestions = (): JSX.Element | null => {
        if (!isSuggestionsOpen) return null;
        
        if (isLoading) {
            return (
                <ul className={styles.suggestions}>
                    <li className={styles.suggestionItem} style={{ display: 'flex', justifyContent: 'center'}}>
                        <Loader type="SMALL_SPINNER" />
                    </li>
                </ul>
            );
        }
        if (!suggestions || suggestions.instruments.length === 0) {
            return (
                <ul className={styles.suggestions}>
                    {renderNotFoundButton()}
                    <li className={styles.errorMessage}>
                        Aucun résultat pour cette recherche, mais pas d'inquiétude, vous pouvez la saisir manuellement
                    </li>
                </ul>
            );
        }
        return (
            <ul className={styles.suggestions}>
                {renderNotFoundButton()}
                {suggestions.instruments.map((instrument, index) => (
                    <li
                        key={index}
                        onClick={() => handleSelectCompany(instrument)}
                        className={styles.suggestionItem}
                    >
                        <p className={styles.suggestionName}>{instrument.name}</p>
                        <p className={styles.suggestionIsin}>{instrument.ticker} - {instrument.isin}</p>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className={styles.container}>
            <Input
                title="Chercher une société"
                type="text"
                width="100%"
                isSearch
                value={query.trim() === '' ? '' : query}
                onChange={handleInputChange}
                placeholder="Ex : L'Oréal"
                clearInput={handleClear}
            />
            {renderSuggestions()}
        </div>
    );
};

export default CompanySearch;
